import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, TextField, Input, Label } from '@heroui/react';
import { FeatherIcon } from '../components/FeatherIcon';
import { BusyLabel } from '../components/Spinner';
import { AuthShell } from '../brand/AuthShell';
import { Wordmark } from '../brand/Brand';

export function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(identifier.trim(), password.trim());
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
        <div className="bg-[var(--color-bg-card)]/90 backdrop-blur-xl border border-[var(--color-border-default)] rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8 flex flex-col items-center">
            <h1>
              <Wordmark height={26} label={t('brand')} className="om-auth-wordmark" />
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <TextField className="space-y-1.5">
              <Label className="text-sm text-[var(--color-text-secondary)]">{t('login.identifierLabel')}</Label>
              <Input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t('login.identifierPlaceholder')}
                required
                autoComplete="email"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent-primary)] focus:outline-none transition-colors"
              />
            </TextField>

            <TextField className="space-y-1.5">
              <Label className="text-sm text-[var(--color-text-secondary)]">{t('login.passwordLabel')}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
                autoComplete="current-password"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent-primary)] focus:outline-none transition-colors"
              />
            </TextField>

            <Button
              type="submit"
              isDisabled={loading}
              className="w-full py-2.5 rounded-lg bg-[var(--color-accent-primary)] hover:bg-[var(--color-accent-primary)]/90 text-white font-medium transition-colors flex items-center justify-center gap-2"
            >
              <BusyLabel busy={loading} label={t('login.signingIn')}>
                <span className="flex items-center justify-center gap-2">
                  <FeatherIcon name="log-in" size={16} />
                  {t('login.signIn')}
                </span>
              </BusyLabel>
            </Button>

            <p className="text-center text-sm text-[var(--color-text-secondary)]">
              {t('login.noAccount')}{' '}
              <Link to="/signup" className="text-[var(--color-accent-primary)] hover:underline">
                {t('login.signUpLink')}
              </Link>
            </p>
          </form>
        </div>
    </AuthShell>
  );
}
